/**
 * Centralized API service for the Inventory Application.
 * Abstraction over standard fetch to handle base URLs, common headers, and error catching.
 */

const BASE_URL = process.env.REACT_APP_INVENTARIO_API_URL || '';

/**
 * Helper to process the response and reject on HTTP error status codes.
 */
async function handleResponse(response) {
  if (!response.ok) {
    let errorMessage = `HTTP Error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData && errorData.message) {
        errorMessage = errorData.message;
      }
    } catch (_) {
      // Fallback if response is not JSON
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

/**
 * Standard HTTP Request Wrapper
 */
async function request(endpoint, options = {}) {
  const url = `${BASE_URL}${endpoint}`;
  
  // Setup default headers
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  // If body is FormData (e.g. image uploads), let the browser set the boundary header automatically
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const config = {
    ...options,
    headers,
  };

  try {
    const response = await fetch(url, config);
    return await handleResponse(response);
  } catch (error) {
    console.error(`API Request Failed on ${url}:`, error);
    throw error;
  }
}

export const apiService = {
  /**
   * GET Request
   */
  get(endpoint, headers = {}) {
    return request(endpoint, { method: 'GET', headers });
  },

  /**
   * POST Request
   */
  post(endpoint, body, headers = {}) {
    const isFormData = body instanceof FormData;
    return request(endpoint, {
      method: 'POST',
      headers,
      body: isFormData ? body : JSON.stringify(body),
    });
  },

  /**
   * PUT Request
   */
  put(endpoint, body, headers = {}) {
    const isFormData = body instanceof FormData;
    return request(endpoint, {
      method: 'PUT',
      headers,
      body: isFormData ? body : JSON.stringify(body),
    });
  },

  /**
   * DELETE Request
   */
  delete(endpoint, headers = {}) {
    return request(endpoint, { method: 'DELETE', headers });
  },
};
